import axios from "axios";
import { useEffect, useRef, useState } from "react";

import infoIcon from "../../assets/images/audio/info.png";
import nextIcon from "../../assets/images/audio/next.png";
import note from "../../assets/images/audio/nota.png";
import pauseIcon from "../../assets/images/audio/pause.png";
import playIcon from "../../assets/images/audio/play.png";
import prevIcon from "../../assets/images/audio/prev.png";
import volumeIcon2 from "../../assets/images/audio/volume2.png";
import localPlaylist from "../../data/playlist.json";
import classes from "./AudioPlayer.module.css";

function AudioPlayer() {
  const [isOpen, setIsOpen] = useState(false);
  const [volumeIsOpen, setVolumeIsOpen] = useState(false);
  const [infoIsOpen, setInfoIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [playlist, setPlaylist] = useState(localPlaylist);

  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/data/playlist.json")
      .then((response) => {
        if (cancelled) return;
        if (Array.isArray(response.data) && response.data.length > 0) {
          setPlaylist(response.data);
        }
      })
      .catch(() => {
        // Fallback locale già attivo
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePanel = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setInfoIsOpen(false);
    }
  };

  const toggleVolume = () => {
    setVolumeIsOpen((prev) => !prev);
  };

  const toggleInfo = () => {
    setInfoIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (playlist.length === 0) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(playlist[0]?.url);
    }
    const audio = audioRef.current;

    const updateCurrentTime = () => setCurrentTime(audio.currentTime);
    const setAudioData = () => {
      setDuration(audio.duration || 0);
      setCurrentTime(audio.currentTime || 0);
    };
    const handleEnded = () => {
      setCurrentTrackIndex((prev) => (prev + 1) % playlist.length);
      setIsPlaying(true);
    };

    audio.addEventListener("timeupdate", updateCurrentTime);
    audio.addEventListener("loadedmetadata", setAudioData);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateCurrentTime);
      audio.removeEventListener("loadedmetadata", setAudioData);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [playlist.length]);

  useEffect(() => {
    if (!audioRef.current || playlist.length === 0) return;

    const audio = audioRef.current;
    const trackUrl = playlist[currentTrackIndex]?.url;
    if (!trackUrl) return;

    audio.src = trackUrl;
    audio.load();
    setCurrentTime(0);
    if (isPlaying) {
      audio
        .play()
        .catch((error) => console.error("Failed to play audio:", error));
    }
  }, [currentTrackIndex]);

  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    if (isPlaying) {
      audio
        .play()
        .catch((error) => console.error("Failed to play audio:", error));
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const togglePlayPause = () => setIsPlaying((prev) => !prev);

  const handleProgressChange = (event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = (event.target.value / 100) * (duration || 0);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const nextTrack = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % playlist.length);
    setIsPlaying(true);
  };

  const prevTrack = () => {
    setCurrentTrackIndex(
      (prev) => (prev - 1 + playlist.length) % playlist.length
    );
    setIsPlaying(true);
  };

  const handlePlaylistItem = (index) => {
    setCurrentTrackIndex(index);
    setIsPlaying(true);
  };

  return (
    <div className={`${classes.audioPlayer} ${isOpen ? classes.open : classes.closed}`}>
      {!isOpen ? (
        <div className={classes.miniPill}>
          <div className={classes.miniTrackInfo} onClick={togglePanel} title="Apri elenco canzoni">
            <img src={note} className={classes.miniNote} alt="Music" />
            <span className={classes.miniTitle}>
              {playlist[currentTrackIndex]?.title || "Audio Player"}
            </span>
          </div>
          <div className={classes.miniControls}>
            <button
              type="button"
              className={`${classes.miniBtn} ${classes.miniPlayBtn} ${isPlaying ? classes.isPlayingBtn : ""}`}
              onClick={togglePlayPause}
              title={isPlaying ? "Pausa" : "Riproduci"}
              aria-label={isPlaying ? "Pausa" : "Riproduci"}
            >
              {isPlaying ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1.5"></rect>
                  <rect x="14" y="4" width="4" height="16" rx="1.5"></rect>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: "2px" }}>
                  <path d="M7 4.5v15a1 1 0 0 0 1.54.84l12-7.5a1 1 0 0 0 0-1.68l-12-7.5A1 1 0 0 0 7 4.5z"></path>
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`${classes.miniBtn} ${classes.miniPlaylistBtn}`}
              onClick={togglePanel}
              title="Elenco canzoni"
              aria-label="Elenco canzoni"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="9" y1="6" x2="20" y2="6"></line>
                <line x1="9" y1="12" x2="20" y2="12"></line>
                <line x1="9" y1="18" x2="20" y2="18"></line>
                <circle cx="4" cy="6" r="1.8" fill="currentColor"></circle>
                <circle cx="4" cy="12" r="1.8" fill="currentColor"></circle>
                <circle cx="4" cy="18" r="1.8" fill="currentColor"></circle>
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className={classes.expandedContent}>
          <div className={classes.header}>
            <div onClick={togglePanel} className={classes.openArrow} title="Riduci in basso">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            <div className={classes.headerTitle}>Mini audio player</div>
            <div className={classes.flex}>
              <div onClick={toggleVolume} className={classes.headerButton} title="Volume">
                <img src={volumeIcon2} alt="Volume" />
              </div>
              <div onClick={toggleInfo} className={classes.headerButton} title="Info">
                <img src={infoIcon} alt="Info" />
              </div>
            </div>
          </div>

          {!infoIsOpen ? (
            <div className={classes.playerBody}>
              <div className={classes.nowPlayingTitle}>
                {playlist[currentTrackIndex]?.title || "Loading..."}
              </div>

              <div className={classes.progressWrapper}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(currentTime / duration) * 100 || 0}
                  onChange={handleProgressChange}
                  className={classes.progressBar}
                />
              </div>

              <div className={classes.trackControls}>
                <img
                  src={prevIcon}
                  className={classes.playButton2}
                  onClick={prevTrack}
                  alt="Prev"
                />
                <img
                  src={isPlaying ? pauseIcon : playIcon}
                  className={classes.playButton2}
                  onClick={togglePlayPause}
                  alt="Play/Pause"
                />
                <img
                  src={nextIcon}
                  className={classes.playButton2}
                  onClick={nextTrack}
                  alt="Next"
                />
              </div>

              {volumeIsOpen && (
                <div className={classes.volumeRow}>
                  <span>Vol:</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={audioRef.current?.volume || 1}
                    onChange={(event) => {
                      if (audioRef.current) audioRef.current.volume = event.target.value;
                    }}
                    className={classes.volumeSliderRow}
                  />
                </div>
              )}

              <div className={classes.spacer}></div>

              <div className={classes.playlistContainer}>
                {playlist.map((track, index) => (
                  <div
                    key={index}
                    onClick={() => handlePlaylistItem(index)}
                    className={`${classes.playlistItem} ${
                      index === currentTrackIndex ? classes.playlistItemActive : ""
                    }`}
                  >
                    <span className={classes.trackName}>{track.title}</span>
                    <span className={classes.trackTime}>{track.time}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={classes.infoContainer}>
              <b>Mini audio player developed in React</b>
              <br /><br />
              Import the playlist by fetching the JSON file.
              <br /><br />
              Audio track are generated by Suno Ai.
              <br />
              Lyrics by Davide Balice
              <br /><br />
              <b>Github:</b><br />
              <a
                href="https://github.com/davidebalice/react-mini-audio-player"
                target="_blank"
                rel="noreferrer"
              >
                github.com/davidebalice/react-mini-audio-player
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AudioPlayer;
